import { valueToColor } from './colormaps.js';
import { getPopulationCount } from './sqlEngine.js';

// ── Mask state ──────────────────────────────────────────────────
let maskRings = null;   // Array de ring-uri [[lon,lat], ...] din GeoJSON
let maskActive = false;

/**
 * Setează poligoanele de mască (ring-urile exterioare ale României).
 * @param {Array} rings - Array de ring-uri, fiecare e un array de [lon, lat]
 */
export function setMaskPolygons(rings) {
  maskRings = rings;
}

/**
 * Activează/dezactivează clipping-ul pe mască.
 */
export function setMaskActive(active) {
  maskActive = active;
}

/**
 * Returnează starea curentă a măștii.
 */
export function isMaskActive() {
  return maskActive;
}

/**
 * Returnează poligoanele de mască (ring-uri).
 */
export function getMaskPolygons() {
  return maskRings;
}

/**
 * Returnează culoarea (RGBA) pentru o anumită valoare de populație (densitate).
 */
function getPopColor(pop) {
  if (pop <= 0) return [0, 0, 0, 0];
  
  const stops = [
    { p: 0, c: [255, 255, 204, 200] },
    { p: 10, c: [255, 237, 160, 200] },
    { p: 100, c: [254, 217, 118, 200] },
    { p: 500, c: [254, 178, 76, 210] },
    { p: 1000, c: [253, 141, 60, 220] },
    { p: 5000, c: [252, 78, 42, 230] },
    { p: 10000, c: [227, 26, 28, 240] },
    { p: 50000, c: [177, 0, 38, 255] }
  ];

  if (pop >= stops[stops.length - 1].p) return stops[stops.length - 1].c;

  for (let i = 0; i < stops.length - 1; i++) {
    if (pop >= stops[i].p && pop <= stops[i+1].p) {
      const v0 = stops[i].p;
      const v1 = stops[i+1].p;
      const t = (pop - v0) / (v1 - v0);
      const c0 = stops[i].c;
      const c1 = stops[i+1].c;
      return [
        Math.round(c0[0] * (1 - t) + c1[0] * t),
        Math.round(c0[1] * (1 - t) + c1[1] * t),
        Math.round(c0[2] * (1 - t) + c1[2] * t),
        Math.round(c0[3] * (1 - t) + c1[3] * t)
      ];
    }
  }
  return stops[stops.length - 1].c;
}

/**
 * Desenează felia time[t] pe un canvas invizibil și returnează data URL.
 * Dacă masca e activă, pixelii din afara României devin transparenți.
 * @param {{ data: TypedArray, shape: number[], lats: Array, lons: Array }} cube - Cubul de date
 * @param {number} t - Indexul timestep-ului
 * @param {string} variable - Numele variabilei (pentru colormap)
 * @param {Set<number>} [selectedIds] - Set de ID-uri de celule selectate prin crossfilter
 * @param {boolean} [showPopulation] - Dacă este true, colorează pe baza populației
 * @returns {string} Data URL (PNG)
 */
export function sliceToCanvas(cube, t, variable, selectedIds = null, showPopulation = false) {
  const [nt, ny, nx] = cube.shape;
  
  // Noul grid de 1km este de 10 ori mai fin decât cel de 10km (CAMS)
  const subNx = nx * 10;
  const subNy = ny * 10;

  const cv = document.createElement('canvas');
  cv.width = subNx;
  cv.height = subNy;
  const ctx = cv.getContext('2d');
  const img = ctx.createImageData(subNx, subNy);

  const base = t * ny * nx;

  const clampX = (x) => Math.max(0, Math.min(nx - 1, x));
  const clampY = (y) => Math.max(0, Math.min(ny - 1, y));

  const getInterpolatedValue = (j, i) => {
    // Coordonate sub-grid (1km) în spațiul grilei CAMS (10km)
    // Centrul primei celule CAMS e la sub-index 4.5
    const cx = (i - 4.5) / 10;
    const cy = (j - 4.5) / 10;
    
    const x0 = Math.floor(cx);
    const y0 = Math.floor(cy);
    
    const cx0 = clampX(x0);
    const cx1 = clampX(x0 + 1);
    const cy0 = clampY(y0);
    const cy1 = clampY(y0 + 1);

    const v00 = cube.data[base + cy0 * nx + cx0];
    const v10 = cube.data[base + cy0 * nx + cx1];
    const v01 = cube.data[base + cy1 * nx + cx0];
    const v11 = cube.data[base + cy1 * nx + cx1];

    const dx = cx - x0;
    const dy = cy - y0;

    const v0 = v00 * (1 - dx) + v10 * dx;
    const v1 = v01 * (1 - dx) + v11 * dx;
    
    return v0 * (1 - dy) + v1 * dy;
  };

  for (let j = 0; j < subNy; j++) {
    // Latitudine crescătoare în date (sud→nord); imaginea are y=0 sus
    const imgRow = (subNy - 1 - j) * subNx;
    
    for (let i = 0; i < subNx; i++) {
      const p = (imgRow + i) * 4;
      
      // Indexul global 1D pentru celula de 1km
      const subIdx = j * subNx + i;

      // Mask crossfilter: dacă avem filtre și celula NU e selectată → transparent
      if (selectedIds && !selectedIds.has(subIdx)) {
        img.data[p + 3] = 0;
        continue;
      }

      let r, g, b, a;
      if (showPopulation) {
        // Populația e stocată la rezoluția fină (1km)
        const pop = getPopulationCount(j, i);
        [r, g, b, a] = getPopColor(pop);
      } else {
        // Poluarea interpolată biliniar la 1km pentru afișare netedă
        const v = getInterpolatedValue(j, i);
        [r, g, b, a] = valueToColor(v, variable);
      }

      img.data[p] = r;
      img.data[p + 1] = g;
      img.data[p + 2] = b;
      img.data[p + 3] = a;
    }
  }

  ctx.putImageData(img, 0, 0);

  // Aplicare mască România — clip pe canvas (care acum e deja hi-res)
  if (maskActive && maskRings && maskRings.length > 0) {
    const hiRes = applyCanvasMask(cv, cube.lats, cube.lons, subNx, subNy);
    return hiRes.toDataURL('image/png');
  }

  return cv.toDataURL('image/png');
}

/**
 * Aplică mască pe canvas cu upscaling pentru margini netede.
 * 
 * Pași:
 * 1. Upscalează canvas-ul mic (rezoluție Zarr) la rezoluție mare (8x)
 *    cu interpolare bilineară (imageSmoothingEnabled)
 * 2. Aplică clip path din contururile României la rezoluția mare
 * 3. Returnează canvas-ul mare cu margini netede
 * 
 * @returns {HTMLCanvasElement} Canvas upscalat și clip-uit
 */
const UPSCALE = 1; // Canvas-ul de bază e deja 10x (rezoluție 1km), nu mai trebuie upscalat!

function applyCanvasMask(lowResCanvas, lats, lons, nx, ny) {
  const hx = nx * UPSCALE;
  const hy = ny * UPSCALE;

  // 1. Nu mai e nevoie de upscaling suplimentar dacă UPSCALE e 1
  const hiCanvas = lowResCanvas;
  
  // 2. Canvas final — clip path + redraw
  const outCanvas = document.createElement('canvas');
  outCanvas.width = hx;
  outCanvas.height = hy;
  const outCtx = outCanvas.getContext('2d');

  // Mapping coordonate → pixeli la rezoluția mare
  // IMPORTANT: MapLibre proiectează imaginea în Mercator, deci trebuie
  // să folosim aceeași proiecție Mercator pentru axa Y (latitudine).
  // Fără aceasta, clip boundary-ul are un shift de ~0.01° (vizibil pe hartă).
  const lonMin = Number(lons[0]);
  const lonMax = Number(lons[lons.length - 1]);
  const latMin = Number(lats[0]);
  const latMax = Number(lats[lats.length - 1]);
  const dLon = (lonMax - lonMin) / (lons.length - 1) / 2;
  const dLat = (latMax - latMin) / (lats.length - 1) / 2;

  // Bounds extinse (identice cu cele trimise la MapLibre)
  const extLonMin = lonMin - dLon;
  const extLonMax = lonMax + dLon;
  const extLatMin = latMin - dLat;
  const extLatMax = latMax + dLat;
  const extW = extLonMax - extLonMin;

  // Proiecție Mercator pe axa Y
  const mercTop = latToMercY(extLatMax);
  const mercBot = latToMercY(extLatMin);
  const mercH = mercTop - mercBot;

  // 3. Construim clip path la rezoluția mare
  outCtx.beginPath();

  for (const ring of maskRings) {
    for (let k = 0; k < ring.length; k++) {
      const lon = ring[k][0];
      const lat = ring[k][1];

      // X: liniar (longitudinea e liniară și în Mercator)
      const px = (lon - extLonMin) / extW * hx;
      // Y: proiecție Mercator (match cu MapLibre)
      const py = (mercTop - latToMercY(lat)) / mercH * hy;

      if (k === 0) {
        outCtx.moveTo(px, py);
      } else {
        outCtx.lineTo(px, py);
      }
    }
    outCtx.closePath();
  }

  outCtx.clip();

  // 4. Desenăm imaginea upscalată prin clip → margini netede
  outCtx.drawImage(hiCanvas, 0, 0);

  return outCanvas;
}

/**
 * Conversie latitudine (grade) → Mercator Y.
 * Formula standard Web Mercator: y = ln(tan(π/4 + φ/2))
 */
function latToMercY(lat) {
  const rad = lat * Math.PI / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
}

/**
 * Calculează colțurile (bounds) pentru MapLibre image source.
 * Ordinea MapLibre: [top-left, top-right, bottom-right, bottom-left]
 * Fiecare e [lon, lat].
 */
export function computeBounds(lats, lons) {
  const lonMin = Number(lons[0]);
  const lonMax = Number(lons[lons.length - 1]);
  const latMin = Number(lats[0]);
  const latMax = Number(lats[lats.length - 1]);

  // Mică extindere la marginile pixelilor (jumătate de pixel)
  const dLon = (lonMax - lonMin) / (lons.length - 1) / 2;
  const dLat = (latMax - latMin) / (lats.length - 1) / 2;

  return [
    [lonMin - dLon, latMax + dLat],   // top-left
    [lonMax + dLon, latMax + dLat],   // top-right
    [lonMax + dLon, latMin - dLat],   // bottom-right
    [lonMin - dLon, latMin - dLat],   // bottom-left
  ];
}
